import { useState } from 'react';
import { RefreshCw, X } from 'lucide-react';
import { api } from '../api/client';
import { useT } from '../i18n';

/**
 * Dev only (DEPLOY_ENV=dev + PROD_RO_DATABASE_URL): reload this environment's
 * database from a read-only copy of prod, so dev shows live data. The admin
 * must type RESET to confirm; the page reloads afterwards. Hidden on prod
 * (the server reports canRefreshFromProd=false and answers 404).
 */
export function DevDataPanel({ lastRefreshed, onRefreshed }: { lastRefreshed: string | null; onRefreshed: (iso: string) => void }) {
  const { t } = useT();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);

  const run = async () => {
    setBusy(true);
    try {
      const r = await api.post<{ refreshedAt: string; tables: number }>('/api/admin/db-refresh');
      onRefreshed(r.refreshedAt);
      setOpen(false);
      setText('');
      alert(t('admin.devData.done'));
      window.location.reload();
    } catch (err: any) {
      alert(err?.message || t('admin.devData.failed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card">
      <h3 className="card-title mb-md">{t('admin.devData.title')}</h3>
      <p className="text-sm text-muted" style={{ marginBottom: 12 }}>{t('admin.devData.help')}</p>
      <p className="text-sm" style={{ marginBottom: 12 }}>
        {t('admin.devData.lastReload')}{' '}
        <strong>{lastRefreshed ? new Date(lastRefreshed).toLocaleString() : t('admin.devData.never')}</strong>
      </p>
      <button className="btn btn-accent" onClick={() => setOpen(true)}>
        <RefreshCw size={14} /> {t('admin.devData.button')}
      </button>

      {open && (
        <div className="modal-overlay" onClick={() => !busy && setOpen(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">{t('admin.devData.modalTitle')}</h3>
              <button className="close-btn" onClick={() => !busy && setOpen(false)} aria-label={t('common.close')}><X size={16} /></button>
            </div>
            <div className="modal-body">
              <p className="text-sm" style={{ marginBottom: 12 }}>{t('admin.devData.modalWarning')}</p>
              <div className="form-group">
                <label className="form-label">{t('admin.devData.typeReset')}</label>
                <input className="form-input" value={text} onChange={(e) => setText(e.target.value)} placeholder="RESET" autoFocus />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" disabled={busy} onClick={() => setOpen(false)}>{t('common.cancel')}</button>
              <button className="btn btn-accent" disabled={text !== 'RESET' || busy} onClick={run}>
                {busy ? <><RefreshCw size={14} className="spin" /> {t('admin.devData.running')}</> : t('admin.devData.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
